import * as React from 'react'

import { Timestamp } from '@bufbuild/protobuf/wkt'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react'

import {
  Alert,
  IconButton,
  Menu,
  SecondaryButton,
  SideMenuLayoutBreadcrumb,
  SideMenuLayoutContentLayout,
  Tag,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import {
  FaCopyRegular,
  FaEllipsisRegular,
  FaXmarkRegular,
} from '@enechain/ecloud-icons'

import { ApiKeyStatus } from '~/gen/proto/bff/v1/model_pb'
import { ApiKeyStatusTag } from '~/pages/ApiKeys/components/ApiKeysTable/ApiKeyStatusTag'
import { DescriptionList } from '~/pages/ApiKeys/components/DescriptionList'
import RevokeApiKeyModal from '~/pages/ApiKeys/components/RevokeApiKeyModal'
import { useApiKey } from '~/pages/ApiKeys/hooks/useApiKey'
import { useApiKeyRoles } from '~/pages/ApiKeys/hooks/useApiKeyRoles'
import { fromTimestamp } from '~/utils/date'

const formatOrFallback = (
  timestamp: Timestamp | undefined,
  fallback: string,
): string => {
  return timestamp ? fromTimestamp(timestamp).formatYYYYMMDDHHmm() : fallback
}

export const ApiKeyDetail: React.FC<{ apiKeyId: string }> = ({ apiKeyId }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const apiKey = useApiKey(apiKeyId)
  // 契約外になったロールは付与可能一覧に無いので、リンクにせず名前だけ表示する
  const grantableRoleIds = new Map(
    useApiKeyRoles('').map((role) => [role.name, role.id]),
  )
  const revokeDisclosure = useDisclosure()
  const isInactive = apiKey.status === ApiKeyStatus.INACTIVE

  return (
    <SideMenuLayoutContentLayout
      header={
        <Flex alignItems="center" justifyContent="space-between" width="full">
          <SideMenuLayoutBreadcrumb
            items={[
              {
                label: t('pages.api_keys.title'),
                onClick: () => {
                  void navigate({ to: '/api_keys' })
                },
              },
              {
                label: apiKey.name,
                bold: true,
              },
            ]}
          />
        </Flex>
      }
    >
      <Flex
        flexDirection="column"
        gap="3"
        maxWidth="1280px"
        margin="auto"
        padding="2"
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex flexDirection="column" gap="1">
            <Text textStyle="headerLg" color="Text.highEmphasis">
              {apiKey.name}
            </Text>
            <Text textStyle="textMd" color="Text.midEmphasis">
              {t('pages.api_keys.detail.description')}
            </Text>
          </Flex>
          <Flex alignItems="center" gap="2">
            <Tooltip
              label={t('pages.api_keys.detail.editDisabled')}
              isHidden={!isInactive}
            >
              <Box>
                <SecondaryButton
                  disabled={isInactive}
                  onClick={() => {
                    void navigate({
                      to: '/api_keys/$apiKeyId/edit',
                      params: { apiKeyId: apiKey.id },
                    })
                  }}
                >
                  {t('pages.api_keys.action.edit')}
                </SecondaryButton>
              </Box>
            </Tooltip>
            <Menu
              content={[
                {
                  icon: <FaCopyRegular />,
                  label: t('pages.api_keys.action.duplicate'),
                  value: 'duplicate',
                  onClick: () => {
                    void navigate({
                      to: '/api_keys/create',
                      search: { source: apiKey.id },
                    })
                  },
                },
                {
                  icon: <FaXmarkRegular />,
                  label: t('pages.api_keys.action.revoke'),
                  value: 'revoke',
                  variant: 'negative',
                  onClick: revokeDisclosure.onOpen,
                  disabled: isInactive,
                },
              ]}
            >
              <IconButton aria-label="Open Menu">
                <FaEllipsisRegular />
              </IconButton>
            </Menu>
          </Flex>
        </Flex>
        <DescriptionList
          items={[
            {
              label: t('pages.api_keys.label.name'),
              value: (
                <Text textStyle="textMd" fontWeight="bold">
                  {apiKey.name}
                </Text>
              ),
            },
            {
              label: t('pages.api_keys.label.keyPrefix'),
              value: (
                <Text textStyle="textMd" fontFamily="mono">
                  {apiKey.keyPrefix}
                </Text>
              ),
            },
            {
              label: t('pages.api_keys.label.roles'),
              value: (
                <Flex alignItems="center" flexWrap="wrap" gap="2">
                  {apiKey.roleNames.map((roleName) => {
                    const roleId = grantableRoleIds.get(roleName)
                    if (roleId === undefined) {
                      return (
                        <Tag
                          key={roleName}
                          color="Text.accentPrimary"
                          backgroundColor="Surface.accentPrimaryLightLow"
                        >
                          {roleName}
                        </Tag>
                      )
                    }
                    return (
                      <Tag
                        key={roleName}
                        color="Text.accentPrimary"
                        backgroundColor="Surface.accentPrimaryLightLow"
                        cursor="pointer"
                        onClick={() => {
                          void navigate({
                            to: '/api_keys/roles/$roleId',
                            params: { roleId },
                          })
                        }}
                      >
                        {roleName} ›
                      </Tag>
                    )
                  })}
                </Flex>
              ),
            },
            {
              label: t('pages.api_keys.label.status'),
              value: <ApiKeyStatusTag status={apiKey.status} />,
            },
            {
              label: t('pages.api_keys.label.createdAt'),
              value: (
                <Text textStyle="textMd">
                  {formatOrFallback(apiKey.createdAt, '')}
                </Text>
              ),
            },
            {
              label: t('pages.api_keys.label.expiresAt'),
              value: (
                <Text textStyle="textMd">
                  {formatOrFallback(
                    apiKey.expiresAt,
                    t('pages.api_keys.value.noExpiration'),
                  )}
                </Text>
              ),
            },
            {
              label: t('pages.api_keys.label.lastUsedAt'),
              value: (
                <Text textStyle="textMd">
                  {formatOrFallback(
                    apiKey.lastUsedAt,
                    t('pages.api_keys.value.neverUsed'),
                  )}
                </Text>
              ),
            },
            ...(apiKey.revokedAt == null
              ? []
              : [
                  {
                    label: t('pages.api_keys.label.revokedAt'),
                    value: (
                      <Text textStyle="textMd">
                        {formatOrFallback(apiKey.revokedAt, '')}
                      </Text>
                    ),
                  },
                ]),
          ]}
        />
        <Alert status="warning">
          {t('pages.api_keys.detail.credentialNotice')}
        </Alert>
        <RevokeApiKeyModal
          apiKeyId={apiKey.id}
          apiKeyName={apiKey.name}
          open={revokeDisclosure.open}
          onClose={revokeDisclosure.onClose}
          onSuccess={revokeDisclosure.onClose}
        />
      </Flex>
    </SideMenuLayoutContentLayout>
  )
}
